import {
  DEFAULT_TIME_FORMAT,
  formatTimeFromHHmm,
  formatTimeRange as formatDateTimeRange,
  formatTimelineHourLabel,
  parseTimeToMinutes,
} from "utils/dateTime";

export const DEFAULT_SCHEDULE_COLOR = "#03A9F4";
export const TIMELINE_HOUR_HEIGHT = 56;
export const TIMELINE_HOURS = Array.from({ length: 24 }, (_, index) => index);
export const TIMELINE_SNAP_MINUTES = 1;
export const TIMELINE_MIN_CLASS_DURATION_MINUTES = 1;
export const TIMELINE_DAY_END_MINUTES = 23 * 60 + 59;

export { parseTimeToMinutes };

export const formatTimelineLabel = (hour, timeFormat = DEFAULT_TIME_FORMAT) =>
  formatTimelineHourLabel(hour, timeFormat);

export const formatScheduleTimeLabel = (timeString, timeFormat = DEFAULT_TIME_FORMAT) =>
  formatTimeFromHHmm(timeString, timeFormat);

export const formatScheduleTimeRange = (startTime, endTime, timeFormat = DEFAULT_TIME_FORMAT) =>
  formatDateTimeRange(startTime, endTime, timeFormat);

export const clampMinutes = (minutes, min = 0, max = TIMELINE_DAY_END_MINUTES) =>
  Math.max(min, Math.min(max, Number(minutes) || 0));

export const snapMinutes = (minutes) =>
  Math.round((Number(minutes) || 0) / TIMELINE_SNAP_MINUTES) * TIMELINE_SNAP_MINUTES;

export const formatMinutesAsTimeInput = (minutes) => {
  const normalizedMinutes = clampMinutes(minutes);
  const hours = Math.floor(normalizedMinutes / 60);
  const minuteValue = normalizedMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minuteValue).padStart(2, "0")}`;
};

export const normalizeLabelColor = (value, fallback = DEFAULT_SCHEDULE_COLOR) => {
  const normalizedValue = String(value || "").trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(normalizedValue) ? normalizedValue : fallback;
};

export const normalizeHexColor = (value) => {
  const normalizedValue = String(value || "").trim();
  return /^#[0-9A-Fa-f]{6}$/.test(normalizedValue) ? normalizedValue.toUpperCase() : null;
};

export const getContrastTextColor = (hexColor) => {
  const normalizedColor = normalizeHexColor(hexColor);
  if (!normalizedColor) return "#FFFFFF";
  const red = Number.parseInt(normalizedColor.slice(1, 3), 16);
  const green = Number.parseInt(normalizedColor.slice(3, 5), 16);
  const blue = Number.parseInt(normalizedColor.slice(5, 7), 16);
  const yiq = (red * 299 + green * 587 + blue * 114) / 1000;
  return yiq >= 170 ? "#0F172A" : "#FFFFFF";
};

export const periodsOverlap = (startA, endA, startB, endB) =>
  startA < endB && startB < endA;

const normalizeTimelineBlocks = (blocks = [], excludeBlockId = "") => {
  const excludeId = String(excludeBlockId || "");

  return (Array.isArray(blocks) ? blocks : [])
    .map((block, index) => ({
      id: String(block?.id || `block-${index}`),
      startMinutes: parseTimeToMinutes(block?.startTime ?? block?.startMinutes),
      endMinutes: parseTimeToMinutes(block?.endTime ?? block?.endMinutes),
    }))
    .filter(
      (block) =>
        block.id !== excludeId &&
        block.startMinutes !== null &&
        block.endMinutes !== null &&
        block.endMinutes > block.startMinutes
    )
    .sort((leftBlock, rightBlock) => leftBlock.startMinutes - rightBlock.startMinutes);
};

const getOverlapDurationMinutes = (startA, endA, startB, endB) =>
  Math.max(0, Math.min(endA, endB) - Math.max(startA, startB));

const pickPrimaryOverlappingBlock = (overlappingBlocks = [], dragCenterMinutes = 0) =>
  overlappingBlocks
    .slice()
    .sort((leftBlock, rightBlock) => {
      const leftDistance = Math.abs(dragCenterMinutes - ((leftBlock.startMinutes + leftBlock.endMinutes) / 2));
      const rightDistance = Math.abs(dragCenterMinutes - ((rightBlock.startMinutes + rightBlock.endMinutes) / 2));
      if (leftDistance !== rightDistance) {
        return leftDistance - rightDistance;
      }

      return (
        getOverlapDurationMinutes(
          dragCenterMinutes,
          dragCenterMinutes,
          leftBlock.startMinutes,
          leftBlock.endMinutes
        ) -
        getOverlapDurationMinutes(
          dragCenterMinutes,
          dragCenterMinutes,
          rightBlock.startMinutes,
          rightBlock.endMinutes
        )
      );
    })[0];

export function resolveTimelineBlockMove({
  startMinutes,
  endMinutes,
  blocks = [],
  excludeBlockId = "",
}) {
  const durationMinutes = Math.max(
    TIMELINE_MIN_CLASS_DURATION_MINUTES,
    snapMinutes(endMinutes) - snapMinutes(startMinutes)
  );
  let nextStartMinutes = clampMinutes(
    snapMinutes(startMinutes),
    0,
    TIMELINE_DAY_END_MINUTES - durationMinutes
  );
  let nextEndMinutes = nextStartMinutes + durationMinutes;
  const otherBlocks = normalizeTimelineBlocks(blocks, excludeBlockId);
  const dragCenterMinutes = nextStartMinutes + (durationMinutes / 2);

  for (let attempt = 0; attempt < otherBlocks.length + 2; attempt += 1) {
    const overlappingBlocks = otherBlocks.filter((block) =>
      periodsOverlap(nextStartMinutes, nextEndMinutes, block.startMinutes, block.endMinutes)
    );

    if (!overlappingBlocks.length) {
      return {
        startMinutes: nextStartMinutes,
        endMinutes: nextEndMinutes,
      };
    }

    const obstructingBlock = pickPrimaryOverlappingBlock(overlappingBlocks, dragCenterMinutes);
    const obstructingMidpoint = (obstructingBlock.startMinutes + obstructingBlock.endMinutes) / 2;

    if (dragCenterMinutes >= obstructingMidpoint) {
      nextStartMinutes = obstructingBlock.endMinutes;
    } else {
      nextStartMinutes = obstructingBlock.startMinutes - durationMinutes;
    }

    nextStartMinutes = clampMinutes(
      snapMinutes(nextStartMinutes),
      0,
      TIMELINE_DAY_END_MINUTES - durationMinutes
    );
    nextEndMinutes = nextStartMinutes + durationMinutes;
  }

  return {
    startMinutes: nextStartMinutes,
    endMinutes: nextEndMinutes,
  };
}
